import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { TimelineHistoricoComponent, TimelineHistoricoItem } from '@shared/components/timeline-historico/timeline-historico.component';
import { OrigemOcorrenciaEnum } from '@shared/enums/repo-dfse/origem-ocorrencia.enum';
import { StatusFiscalNFSeEntradaEnum } from '@shared/enums/repo-dfse/status-fiscal-nfse-entrada.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ServicoTomadoService } from '@shared/services/apis/repo-dfse/servico-tomado.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { UsuarioService } from '@shared/services/apis/usuario/usuario.service';
import { NFSeEntradaOcorrenciaCriarRequest } from '@shared/services/requests/repo-dfse/nfse-entrada-ocorrencia-criar.request';
import { NFSeEntradaOcorrenciaStruct } from '@shared/services/structs/repo-dfse/nfse-entrada-ocorrencia.struct';
import { HistoricoServicoTomadoDialogData } from './historico-servico-tomado-dialog-data';

/**
 * "Ver histórico" do serviço tomado: as ocorrências do documento em `app-timeline-historico`, com filtros e,
 * para quem pode alterar serviços tomados, a anotação manual. Leitura e anotação direto na API RepoDFSe.
 */
@Component({
  selector: 'app-historico-servico-tomado-dialog',
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButtonModule, MatIconModule, MatProgressSpinnerModule, LoadingBoxComponent, TimelineHistoricoComponent],
  templateUrl: './historico-servico-tomado-dialog.component.html',
  styleUrls: ['./historico-servico-tomado-dialog.component.css'],
})
export class HistoricoServicoTomadoDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<HistoricoServicoTomadoDialogComponent>);
  private readonly servicoTomadoService = inject(ServicoTomadoService);
  private readonly authService = inject(AuthService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly alertService = inject(AlertService);
  readonly data = inject<HistoricoServicoTomadoDialogData>(MAT_DIALOG_DATA);

  @ViewChild(TimelineHistoricoComponent) timeline?: TimelineHistoricoComponent;

  readonly podeAnotar = (this.authService.getUserInfos()?.listaPermissoes ?? []).includes(
    PermissaoEnum.Suprimentos_ServicosTomados_Alterar
  );
  isLoading = true;
  isRegistrandoAnotacao = false;
  itens: TimelineHistoricoItem[] = [];
  /** Nome por id, para as linhas da carga inicial, que não guardavam o nome do usuário. */
  private readonly usuarios = new Map<number, string>();

  ngOnInit(): void {
    this.carregar();
  }

  /** O skeleton é só da primeira carga; depois de anotar, a lista troca no lugar. */
  private carregar(): void {
    forkJoin({
      res: this.servicoTomadoService.getHistorico(this.data.idNFSeEntrada),
      usuarios: this.usuarios.size > 0 ? of(null) : this.usuarioService.getAll().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ res, usuarios }) => {
        this.isLoading = false;
        usuarios?.listaUsuario?.forEach(u => this.usuarios.set(u.idUsuario, u.nomeUsuario));
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.itens = (res.listaOcorrencia ?? []).map(ocorrencia => this.paraItem(ocorrencia));
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar o histórico.', AlertType.error);
      },
    });
  }

  /** Sem nome gravado, o autor vem da lista de usuários; sem usuário, é a origem; a origem só aparece como badge quando não é o próprio usuário. */
  private paraItem(ocorrencia: NFSeEntradaOcorrenciaStruct): TimelineHistoricoItem {
    return {
      icone: ocorrencia.nomeIconeTipoOcorrencia,
      titulo: ocorrencia.nomeTipoOcorrencia,
      descricao: ocorrencia.descricao,
      detalhe: ocorrencia.detalhes,
      justificativa: ocorrencia.justificativa,
      dados: ocorrencia.dados,
      quando: ocorrencia.dataOcorrencia,
      quemNome:
        ocorrencia.nomeUsuario ||
        (ocorrencia.idUsuario != null ? this.usuarios.get(ocorrencia.idUsuario) : null) ||
        ocorrencia.nomeOrigemOcorrencia,
      nomeOrigem: ocorrencia.nomeOrigemOcorrencia,
      iconeOrigem: ocorrencia.nomeIconeOrigemOcorrencia,
      exibirOrigem: ocorrencia.idOrigemOcorrencia !== OrigemOcorrenciaEnum.Usuario,
      nomeStatusAnterior: ocorrencia.nomeStatusFiscalNFSeEntradaAnterior,
      classeStatusAnterior: this.classeStatus(ocorrencia.idStatusFiscalNFSeEntradaAnterior),
      nomeStatusAtual: ocorrencia.nomeStatusFiscalNFSeEntradaAtual,
      classeStatusAtual: this.classeStatus(ocorrencia.idStatusFiscalNFSeEntradaAtual),
    };
  }

  /** Mesmas cores do badge de status da listagem. */
  private classeStatus(idStatusFiscalNFSeEntrada: number | null): string | null {
    if (idStatusFiscalNFSeEntrada == null) return null;
    if (idStatusFiscalNFSeEntrada === StatusFiscalNFSeEntradaEnum.Cancelada) return 'status-badge badge-vermelho';
    if (idStatusFiscalNFSeEntrada === StatusFiscalNFSeEntradaEnum.Regular) return 'status-badge badge-verde';
    return 'status-badge badge-cinza';
  }

  /** Anotação do dia vai sem data, para o servidor gravar a hora do registro; retroativa leva o dia escolhido. */
  registrarAnotacao(anotacao: { descricao: string; dataOcorrencia: Date }): void {
    const request = new NFSeEntradaOcorrenciaCriarRequest();
    request.descricao = anotacao.descricao;
    request.dataOcorrencia = anotacao.dataOcorrencia.toDateString() === new Date().toDateString() ? null : anotacao.dataOcorrencia;

    this.isRegistrandoAnotacao = true;
    this.servicoTomadoService.criarOcorrencia(this.data.idNFSeEntrada, request).subscribe({
      next: res => {
        this.isRegistrandoAnotacao = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.timeline?.limparAnotacao();
        this.alertService.show('Sucesso', 'Anotação registrada.', AlertType.success);
        this.carregar();
      },
      error: () => {
        this.isRegistrandoAnotacao = false;
        this.alertService.show('Erro', 'Não foi possível registrar a anotação.', AlertType.error);
      },
    });
  }

  fechar(): void {
    this.dialogRef.close();
  }
}
